import { addDays } from 'date-fns';

export type MoonPhaseName =
  | 'New Moon'
  | 'Waxing Crescent'
  | 'First Quarter'
  | 'Waxing Gibbous'
  | 'Full Moon'
  | 'Waning Gibbous'
  | 'Last Quarter'
  | 'Waning Crescent';

export interface MoonPhase {
  name: MoonPhaseName;
  illumination: number; // 0–100
  ageDays: number;
  cycleDays: number;
  daysUntilFull: number;
  daysUntilNew: number;
  nextFullMoon: Date;
  nextNewMoon: Date;
  note: string;
}

// Simple lunar phase calculation (synodic month ~29.53 days)
const LUNAR_CYCLE_DAYS = 29.53058867;
const KNOWN_NEW_MOON = new Date('2000-01-06T18:14:00Z');

function getLunarAge(date: Date): number {
  const elapsed = date.getTime() - KNOWN_NEW_MOON.getTime();
  const days = elapsed / (1000 * 60 * 60 * 24);
  return ((days % LUNAR_CYCLE_DAYS) + LUNAR_CYCLE_DAYS) % LUNAR_CYCLE_DAYS;
}

function getPhaseName(age: number): MoonPhaseName {
  if (age < 1.85) return 'New Moon';
  if (age < 7.38) return 'Waxing Crescent';
  if (age < 9.23) return 'First Quarter';
  if (age < 14.77) return 'Waxing Gibbous';
  if (age < 16.61) return 'Full Moon';
  if (age < 22.15) return 'Waning Gibbous';
  if (age < 24.0) return 'Last Quarter';
  if (age < 29.53) return 'Waning Crescent';
  return 'New Moon';
}

function illuminationPercent(age: number): number {
  const rad = (age / LUNAR_CYCLE_DAYS) * Math.PI * 2;
  return Math.round(50 + 50 * Math.cos(rad - Math.PI));
}

const PHASE_NOTES: Record<MoonPhaseName, string> = {
  'New Moon':
    'The Moon sits between Earth and the Sun, so the night side faces us and the disc looks dark. Best for dark-sky viewing.',
  'Waxing Crescent':
    'A thin sliver is growing. Look west after sunset — the Moon follows the Sun down.',
  'First Quarter':
    'Half the disc is lit and still growing. Highest around sunset, setting around midnight.',
  'Waxing Gibbous':
    'More than half lit and approaching full. Bright in the evening and much of the night.',
  'Full Moon':
    'Earth is between the Sun and Moon, so the near side is fully lit. Rises around sunset and is up all night.',
  'Waning Gibbous':
    'Still bright but shrinking. Rises after sunset and is well placed in the late night and morning sky.',
  'Last Quarter':
    'Half the disc is lit and shrinking. Highest around sunrise; a morning Moon.',
  'Waning Crescent':
    'A fading sliver before the next new Moon. Look east in the hours before dawn.',
};

export function getMoonPhase(date: Date = new Date()): MoonPhase {
  const age = getLunarAge(date);
  const name = getPhaseName(age);
  const illumination = illuminationPercent(age);

  const daysToFull = (14.77 - age + LUNAR_CYCLE_DAYS) % LUNAR_CYCLE_DAYS;
  const daysToNew = (LUNAR_CYCLE_DAYS - age) % LUNAR_CYCLE_DAYS;

  const nextFull = addDays(date, Math.ceil(daysToFull));
  const nextNew = addDays(date, Math.ceil(daysToNew));

  return {
    name,
    illumination,
    ageDays: Math.round(age * 10) / 10,
    cycleDays: Math.round(LUNAR_CYCLE_DAYS * 10) / 10,
    daysUntilFull: Math.ceil(daysToFull),
    daysUntilNew: Math.ceil(daysToNew),
    nextFullMoon: nextFull,
    nextNewMoon: nextNew,
    note: PHASE_NOTES[name],
  };
}
